import { signUpService } from './auth';
import { createProductService, createProductImagesService } from './products';
import { getStoreService, createStoreImagesService, updateCurrencyService } from './store';
import {
    createCollectionsService,
    createSubCollection,
    updateCollectionsServiceImage,
} from './collections';
import { createPricingTierService, parseTierIdFromResponse } from './pricingTiers';
import { logIn } from '../modules/auth';
import { cleanDbName, normalizeSpaces } from '../utils/dbText';

/**
 * API used by signup v2. `realApi` wraps the existing services; `createDemoApi` exposes the same
 * interface without any network write (and without reads, so the demo is deterministic).
 */

const DEMO_LATENCY_MS = 800;

const ONBOARDING_QUESTIONS_QUERY = '{ onboarding { id onboardingOptions { id order } } }';

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const demoError = (status = 500, data = { message: 'demo failure' }) => {
    const error = new Error(`Demo failure (${status})`);
    error.response = { status, data };
    return error;
};

export const realApi = {
    isDemo: false,
    signUp: (payload) => signUpService(payload),
    /** Dispatches the existing `logIn` thunk. `onLogin` runs before `LOGIN` is dispatched. */
    logIn: (dispatch, email, password, onLogin, onError) =>
        dispatch(logIn(email, password, onLogin, onError)),
    getStore: (storeId) => getStoreService(storeId).then(({ data }) => data),
    createProduct: (storeId, payload) => createProductService(storeId, payload),
    /** One request with every photo (`files` multipart field); the first becomes the main image. */
    uploadProductImages: (storeId, productId, files) =>
        createProductImagesService(storeId, productId, files),
    updateStoreLogo: (storeId, file) => createStoreImagesService(storeId, [file]),
    updateStoreBanner: (storeId, file) => createStoreImagesService(storeId, [file], true),
    updateCurrency: (storeId, currency) => updateCurrencyService(storeId, currency),
    createCollection: (storeId, name) =>
        createCollectionsService(storeId, {
            name,
            id: null,
            description: '',
            public: 1,
            products: [],
        }),
    uploadCollectionImage: (storeId, collectionId, file) => {
        const formData = new FormData();
        formData.append('files', file);
        return updateCollectionsServiceImage(storeId, collectionId, formData);
    },
    createSubCollection: (storeId, parentId, name) => createSubCollection(storeId, parentId, name),
    createPricingTier: (storeId, payload) => createPricingTierService(storeId, payload),
    /** Onboarding questions and options (the niche ids for `on_boarding_options`). */
    getOnboardingQuestions: () =>
        fetch(process.env.REACT_APP_GRAPH_QL_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: ONBOARDING_QUESTIONS_QUERY }),
        })
            .then((response) => response.json())
            .then((body) => (body && body.data && body.data.onboarding) || []),
};

/**
 * @param {Object} options
 * @param {string|null} options.failure forced failure (`signup|email|storename|phone|login|product|photo|store`)
 * @param {function(): Object} options.getDemoStore builds the fake store shown on the last step
 */
export const createDemoApi = ({ failure, getDemoStore }) => {
    let demoProductId = 1000;
    let demoCollectionId = 2000;

    return {
        isDemo: true,
        signUp: async () => {
            await wait(DEMO_LATENCY_MS);
            if (failure === 'signup') throw demoError(500);
            if (failure === 'email') return { status: 200, data: { error_code: 202 } };
            if (failure === 'storename') {
                throw demoError(409, { error_code: 'STORE_NAME_TAKEN', field: 'store' });
            }
            if (failure === 'phone') {
                throw demoError(409, { error_code: 'PHONE_IN_USE', field: 'phone' });
            }
            return { status: 200, data: {} };
        },
        logIn: (dispatch, email, password, onLogin, onError) => {
            wait(DEMO_LATENCY_MS / 2).then(() => {
                if (failure === 'login') {
                    onError(demoError(401));
                    return;
                }
                onLogin();
            });
        },
        getStore: async () => {
            await wait(DEMO_LATENCY_MS);
            if (failure === 'store') throw demoError(500);
            return getDemoStore();
        },
        createProduct: async () => {
            await wait(DEMO_LATENCY_MS);
            if (failure === 'product') throw demoError(500);
            demoProductId += 1;
            return { status: 200, data: { id: demoProductId } };
        },
        uploadProductImages: async () => {
            await wait(DEMO_LATENCY_MS);
            if (failure === 'photo') throw demoError(500);
            return { status: 200, data: {} };
        },
        updateStoreLogo: async () => {
            await wait(DEMO_LATENCY_MS);
            return { status: 200, data: {} };
        },
        updateStoreBanner: async () => {
            await wait(DEMO_LATENCY_MS);
            return { status: 200, data: {} };
        },
        updateCurrency: async () => {
            await wait(DEMO_LATENCY_MS / 2);
            return { status: 200, data: {} };
        },
        createCollection: async () => {
            await wait(DEMO_LATENCY_MS);
            demoCollectionId += 1;
            return { status: 200, data: { id: demoCollectionId } };
        },
        uploadCollectionImage: async () => {
            await wait(DEMO_LATENCY_MS);
            return { status: 200, data: {} };
        },
        createSubCollection: async () => {
            await wait(DEMO_LATENCY_MS);
            demoCollectionId += 1;
            return { status: 200, data: { id: demoCollectionId } };
        },
        createPricingTier: async () => {
            await wait(DEMO_LATENCY_MS);
            return { status: 200, data: { id: 'demo-tier' } };
        },
        // No read: the demo niche is saved without a backend option id.
        getOnboardingQuestions: async () => [],
    };
};

/** Store-wide percentage rule, same as Promociones → "Descuento por cantidad" → "Toda la tienda". */
export const buildWholesaleTierPayload = (thresholds) => ({
    name: 'Precios por mayor',
    scope: 'store',
    discount_type: 'percentage',
    thresholds,
    active: true,
});

/**
 * After the account exists, create everything captured during signup v2: store currency, logo and
 * banner, one collection per category (+ photo + subcategories as child collections), every product
 * (+ photos, in its category) and, for wholesale stores, the store-wide volume pricing rule.
 * Resilient by design: each call is independent, a failure is recorded in `errors` and never
 * blocks the others or the success screen. Returns a summary for analytics / onboarding state.
 *
 * @param {Object} api realApi or demoApi
 * @param {number} storeId store the JWT belongs to
 * @param {Object} data { currency, categories[], categoryFiles{id:File}, logoFile, bannerFile,
 *   products[{name, price, categoryId, files[]}], wholesale }
 *   `categoryId` is a category or subcategory id from the signup (mapped to the created collection).
 *   `wholesale` = { thresholds: [{ min_quantity, discount_value }] } (percentage) or null.
 *   `product` (single, older drafts) is still accepted.
 */
export const finalizeOnboardingV2 = async (api, storeId, data) => {
    const {
        currency = null,
        categories = [],
        categoryFiles = {},
        logoFile = null,
        bannerFile = null,
        products: productList = null,
        product = null,
        wholesale = null,
    } = data || {};
    const products = Array.isArray(productList) ? productList : product ? [product] : [];
    const summary = { collectionIds: [], productId: null, productIds: [], pricingTierId: null, errors: [] };
    const statusOf = (error) => (error && error.response && error.response.status) || null;
    // signup category / subcategory id → created collection id
    const collectionFor = {};

    if (currency) {
        try {
            await api.updateCurrency(storeId, currency);
        } catch (error) {
            summary.errors.push({ stage: 'currency', status: statusOf(error) });
        }
    }

    if (bannerFile) {
        try {
            await api.updateStoreBanner(storeId, bannerFile);
        } catch (error) {
            summary.errors.push({ stage: 'banner', status: statusOf(error) });
        }
    }

    if (logoFile) {
        try {
            await api.updateStoreLogo(storeId, logoFile);
        } catch (error) {
            summary.errors.push({ stage: 'logo', status: statusOf(error) });
        }
    }

    for (const category of categories) {
        const name = normalizeSpaces(category && (category.label || category.name));
        if (!name) continue;
        try {
            const res = await api.createCollection(storeId, name);
            const collectionId = res && res.data && res.data.id;
            if (collectionId) {
                summary.collectionIds.push(collectionId);
                collectionFor[category.id] = collectionId;
                const file = category && categoryFiles[category.id];
                if (file) {
                    try {
                        await api.uploadCollectionImage(storeId, collectionId, file);
                    } catch (error) {
                        summary.errors.push({
                            stage: 'collection_image',
                            id: category.id,
                            status: statusOf(error),
                        });
                    }
                }
                const subcategories = Array.isArray(category.subcategories) ? category.subcategories : [];
                for (const sub of subcategories) {
                    const subName = normalizeSpaces(sub && sub.label);
                    if (!subName) continue;
                    try {
                        const subRes = await api.createSubCollection(storeId, collectionId, subName);
                        const subId = subRes && subRes.data && subRes.data.id;
                        if (subId) collectionFor[sub.id] = subId;
                    } catch (error) {
                        summary.errors.push({ stage: 'subcollection', id: sub.id, status: statusOf(error) });
                    }
                }
            }
        } catch (error) {
            summary.errors.push({ stage: 'collection', id: category && category.id, status: statusOf(error) });
        }
    }

    for (const item of products) {
        if (!item || !cleanDbName(item.name)) continue;
        const target = item.categoryId ? collectionFor[item.categoryId] : null;
        const payload = {
            name: cleanDbName(item.name),
            price: Number(item.price) || 0,
            sku: '',
            description: '',
            video_url: null,
            discount_price: null,
            active: 1,
            tags: '',
            collections_id: target ? [target] : [],
            quantity_available: null,
            product_options: null,
            product_variants: [],
            product_images: [],
        };
        try {
            const res = await api.createProduct(storeId, payload);
            const productId = res && res.data && res.data.id;
            if (productId) {
                summary.productIds.push(productId);
                if (!summary.productId) summary.productId = productId;
            }
            const files = (Array.isArray(item.files) ? item.files : [item.file]).filter(Boolean);
            if (productId && files.length) {
                try {
                    const uploaded = await api.uploadProductImages(storeId, productId, files);
                    // The API skips files it can't store (still 200): report a short count.
                    const stored = uploaded && Array.isArray(uploaded.data) ? uploaded.data.length : files.length;
                    if (stored < files.length) {
                        summary.errors.push({ stage: 'product_image', status: 'partial', stored, sent: files.length });
                    }
                } catch (error) {
                    summary.errors.push({ stage: 'product_image', status: statusOf(error) });
                }
            }
        } catch (error) {
            summary.errors.push({ stage: 'product', status: statusOf(error) });
        }
    }

    if (wholesale && Array.isArray(wholesale.thresholds) && wholesale.thresholds.length) {
        try {
            const res = await api.createPricingTier(storeId, buildWholesaleTierPayload(wholesale.thresholds));
            summary.pricingTierId = parseTierIdFromResponse(res && res.data);
        } catch (error) {
            summary.errors.push({ stage: 'pricing_tier', status: statusOf(error) });
        }
    }

    return summary;
};
